import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Button, Select, Slider, Row, Col, Tag, Empty, Spin, Alert,
  message, Space, Typography, Avatar,
} from 'antd';
import {
  SearchOutlined, EnvironmentOutlined, UserOutlined, ReloadOutlined,
  StarOutlined, MessageOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Title, Text } = Typography;

const SKILL_OPTIONS = [
  { value: '', label: 'Qualquer nível' },
  { value: 'iniciante', label: 'Iniciante' },
  { value: 'intermediario', label: 'Intermediário' },
  { value: 'avancado', label: 'Avançado' },
];
const POSITION_OPTIONS = [
  { value: '', label: 'Qualquer posição' },
  { value: 'goleiro', label: 'Goleiro' },
  { value: 'zagueiro', label: 'Zagueiro' },
  { value: 'lateral', label: 'Lateral' },
  { value: 'meia', label: 'Meia' },
  { value: 'atacante', label: 'Atacante' },
];

// Procurar JOGADORES no contexto do dono de time-comunidade. Endpoint proprio
// (owner_player_id), sem dependencia do fluxo Futezap.
export default function TeamOwnerFindPlayers() {
  const nav = useNavigate();
  const [teamId, setTeamId] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [radius, setRadius] = useState(15);
  const [skill, setSkill] = useState('');
  const [position, setPosition] = useState('');
  const [inviting, setInviting] = useState(null);
  const [invitedIds, setInvitedIds] = useState(new Set());

  const invite = async (p) => {
    if (!teamId) return;
    setInviting(p.id);
    try {
      await api.post(`/community/team-owner/${teamId}/invite-player`, { player_id: p.id });
      setInvitedIds(prev => new Set([...prev, p.id]));
      message.success(`Convite enviado pro ${p.name}`);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Erro ao convidar');
    } finally { setInviting(null); }
  };

  useEffect(() => {
    api.get('/community/team-owner/my').then(r => {
      const t = r.data?.teams?.[0];
      if (t) setTeamId(t.id);
      else setError('Cadastre um time primeiro');
    });
  }, []);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams();
      if (radius) p.set('radius', String(radius));
      if (skill) p.set('skill_level', skill);
      if (position) p.set('position', position);
      const r = await api.get(`/community/team-owner/${teamId}/nearby-players?${p}`);
      setPlayers(r.data.players || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Erro ao buscar');
    } finally { setLoading(false); }
  }, [teamId, radius, skill, position]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <Title level={3} style={{ margin: 0 }}><SearchOutlined /> Procurar jogadores</Title>
        <Text type="secondary">Boleiros públicos e disponíveis próximos do teu time.</Text>
      </div>

      {error && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={error}
          action={<Button size="small" onClick={() => nav('/time/editar/' + teamId)}>Editar time</Button>} />
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <Text style={{ fontSize: 12, color: '#888' }}>Raio: <b>{radius} km</b></Text>
            <Slider min={5} max={100} step={5} value={radius} onChange={setRadius} />
          </Col>
          <Col xs={12} md={6}>
            <Text style={{ fontSize: 12, color: '#888' }}>Nível</Text>
            <Select style={{ width: '100%' }} value={skill} onChange={setSkill} options={SKILL_OPTIONS} />
          </Col>
          <Col xs={12} md={6}>
            <Text style={{ fontSize: 12, color: '#888' }}>Posição</Text>
            <Select style={{ width: '100%' }} value={position} onChange={setPosition} options={POSITION_OPTIONS} />
          </Col>
        </Row>
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => {
            setRadius(15); setSkill(''); setPosition('');
          }}>Limpar filtros</Button>
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : players.length === 0 ? (
        <Card><Empty description={radius < 100 ?
          `Ninguém em ${radius} km com esses filtros. Aumenta o raio ou muda filtros.` :
          'Nenhum boleiro publico por perto — a comunidade tá crescendo.'} /></Card>
      ) : (
        players.map(p => (
          <Card key={p.id} size="small" style={{ marginBottom: 10 }}>
            <Row gutter={12} align="middle" wrap>
              <Col>
                <Avatar size={44} src={p.profile_photo} icon={<UserOutlined />} />
              </Col>
              <Col flex="auto">
                <div style={{ fontWeight: 700, fontSize: 15 }}>{p.name}</div>
                <Space size={6} wrap style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                  <span><EnvironmentOutlined /> {p.distance_km.toFixed(1)} km</span>
                  {p.preferred_position && <Tag>{p.preferred_position}</Tag>}
                  {p.skill_level && <Tag color="blue">{p.skill_level}</Tag>}
                  {p.total_ratings > 0 ? (
                    <Tag color="purple" title={`Média de ${p.total_ratings} avaliações no Futezap`}>
                      <StarOutlined /> {parseFloat(p.average_rating).toFixed(1)} Futezap
                    </Tag>
                  ) : p.average_rating > 0 && (
                    <Tag color="purple"><StarOutlined /> {parseFloat(p.average_rating).toFixed(1)}</Tag>
                  )}
                  {p.total_games > 0 && <Tag>{p.total_games} jogos</Tag>}
                  {p.game_price && Number(p.game_price) > 0 && (
                    <Tag color="gold" title="Valor que o jogador cobra pra ir jogar">
                      R$ {Number(p.game_price).toFixed(2)}
                    </Tag>
                  )}
                </Space>
              </Col>
              <Col>
                {(invitedIds.has(p.id) || p.last_invite_status === 'PENDING') ? (
                  <Button icon={<MessageOutlined />} disabled>Convite enviado</Button>
                ) : p.last_invite_status === 'ACCEPTED' ? (
                  <Button icon={<MessageOutlined />} disabled>Já é do time</Button>
                ) : (
                  <Button type="primary" icon={<MessageOutlined />}
                    loading={inviting === p.id} onClick={() => invite(p)}>
                    {p.last_invite_status === 'REJECTED' ? 'Convidar de novo' : 'Convidar'}
                  </Button>
                )}
              </Col>
            </Row>
          </Card>
        ))
      )}
    </div>
  );
}
